import { IssueModel } from 'shared/domain/issue/issueModel';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { compareObjectIds } from 'shared/utils/id/compare';

type IssueForPdfFieldValue = IssueModel & {
  primaryData: IssueModel['primaryData'] & {
    documents?: Array<{ _id?: string }>;
  };
  linkedDocumentation?: Array<{ deleted?: boolean }>;
};

function hasStringValue(value: string | undefined | null): boolean {
  return Boolean(value && value.length > 0);
}

function hasArrayValue<T>(array: T[] | undefined | null): boolean {
  return Boolean(array && array.length > 0);
}

function hasNumberValue(value: number | undefined | null): boolean {
  return value !== undefined && value !== null;
}

function hasObjectIdValue(value: string | undefined | null): boolean {
  return Boolean(value);
}

function hasPositionOnMap(issue: IssueForPdfFieldValue): boolean {
  const location = issue.primaryData.positionOnMap;
  return location !== undefined && location !== null;
}

/**
 * Frontend port of backend `HasIssueFieldValue` for the page-count heuristic.
 *
 * The backend resolves several fields through `IssueFillData` (de-referenced entities and
 * customized values) which is not available client-side. For those fields we use the raw
 * `IssueModel` value as a presence proxy. This can diverge from the backend when a referenced
 * entity is deleted/unresolved, but it is an accepted approximation for a ±10% page estimate;
 * the rendered field set is otherwise identical to the backend switch.
 */
export function hasIssueFieldValueForPdfEstimate(
  issue: IssueForPdfFieldValue,
  fieldName: IssueFieldNames
): boolean {
  switch (fieldName) {
    case IssueFieldNames.stage:
      return hasObjectIdValue(issue.stageId);
    case IssueFieldNames.assignee:
      return hasObjectIdValue(issue.primaryData.assignee?._id);
    case IssueFieldNames.contractNumbers:
      return hasArrayValue(issue.primaryData.contractNumbers);
    case IssueFieldNames.description:
      return hasStringValue(issue.primaryData.description);
    case IssueFieldNames.detectionDate:
      return hasStringValue(issue.primaryData.detectionDate);
    case IssueFieldNames.documents:
      return hasArrayValue(issue.primaryData.documents);
    case IssueFieldNames.executor:
      return hasObjectIdValue(issue.primaryData.executor?._id);
    case IssueFieldNames.level:
      return hasObjectIdValue(issue.primaryData.level);
    case IssueFieldNames.positionOnMap:
      return hasPositionOnMap(issue);
    case IssueFieldNames.site:
      return hasObjectIdValue(issue.primaryData.site);
    case IssueFieldNames.subcontractors:
      return hasArrayValue(issue.primaryData.subcontractors);
    case IssueFieldNames.title:
      return hasStringValue(issue.primaryData.title);
    case IssueFieldNames.circumstances:
      return hasStringValue(issue.extendedData.circumstances);
    case IssueFieldNames.issueVerification:
      return hasStringValue(issue.extendedData.issueVerification);
    case IssueFieldNames.rejectionReason:
      return hasStringValue(issue.extendedData.rejectionReason);
    case IssueFieldNames.delayReason:
      return hasStringValue(issue.extendedData.delayReason);
    case IssueFieldNames.contaminatedSoilScale:
      return hasNumberValue(issue.extendedData.contaminatedSoilScale);
    case IssueFieldNames.costCode:
      return hasStringValue(issue.extendedData.costCode);
    case IssueFieldNames.daysOfInabilityToWork:
      return hasNumberValue(issue.extendedData.daysOfInabilityToWork);
    case IssueFieldNames.decisionToImposeFine:
      return hasStringValue(issue.extendedData.decisionToImposeFine);
    case IssueFieldNames.effect:
      return hasStringValue(issue.extendedData.effect ?? undefined);
    case IssueFieldNames.environmentalAspect:
      return hasStringValue(issue.extendedData.environmentalAspect ?? undefined);
    case IssueFieldNames.estimatedCost:
      return hasArrayValue(issue.extendedData.estimatedCost);
    case IssueFieldNames.expectedFine:
      return hasNumberValue(issue.extendedData.expectedFine);
    case IssueFieldNames.finalCompletionDate:
      return hasStringValue(issue.extendedData.finalCompletionDate);
    case IssueFieldNames.finalCost:
      return hasArrayValue(issue.extendedData.finalCost);
    case IssueFieldNames.finalStartDate:
      return hasStringValue(issue.extendedData.finalStartDate);
    case IssueFieldNames.hazardCategory:
      return hasArrayValue(issue.extendedData.hazardCategory ?? undefined);
    case IssueFieldNames.impact:
      return hasStringValue(issue.extendedData.impact ?? undefined);
    case IssueFieldNames.imposedFine:
      return hasNumberValue(issue.extendedData.imposedFine);
    case IssueFieldNames.personUnableToWork:
      return hasStringValue(issue.extendedData.personUnableToWork);
    case IssueFieldNames.proposedCorrectiveAction:
      return hasStringValue(issue.extendedData.proposedCorrectiveAction);
    case IssueFieldNames.rootCauses:
      return hasArrayValue(issue.extendedData.rootCauses);
    case IssueFieldNames.soilLeakageScale:
      return hasNumberValue(issue.extendedData.soilLeakageScale);
    case IssueFieldNames.solutionMethod:
      return hasStringValue(issue.extendedData.solutionMethod);
    case IssueFieldNames.solutionProposal:
      return hasStringValue(issue.extendedData.solutionProposal);
    case IssueFieldNames.spilledSubstance:
      return hasStringValue(issue.extendedData.spilledSubstance ?? undefined);
    case IssueFieldNames.subcontractorRepresentative:
      return hasStringValue(issue.extendedData.subcontractorRepresentative);
    case IssueFieldNames.targetAmount:
      return hasNumberValue(issue.extendedData.targetAmount);
    case IssueFieldNames.targetCompletionDate:
      return hasStringValue(issue.extendedData.targetCompletionDate);
    case IssueFieldNames.targetResponseToAuthorDate:
      return hasStringValue(issue.extendedData.targetResponseToAuthorDate);
    case IssueFieldNames.finalResponseToAuthorDate:
      return hasStringValue(issue.extendedData.finalResponseToAuthorDate);
    case IssueFieldNames.targetStartDate:
      return hasStringValue(issue.extendedData.targetStartDate);
    case IssueFieldNames.waterLeakageScale:
      return hasNumberValue(issue.extendedData.waterLeakageScale);
    case IssueFieldNames.completedAmount:
      return hasNumberValue(issue.extendedData.completedAmount);
    case IssueFieldNames.executedByCompanies:
      return hasArrayValue(issue.extendedData.executedByCompanies);
    case IssueFieldNames.workTypes:
      return hasArrayValue(issue.extendedData.workTypes);
    case IssueFieldNames.hashtag:
    case IssueFieldNames.issueNumber:
    case IssueFieldNames.creationDate:
    case IssueFieldNames.createdBy:
    case IssueFieldNames.modificationDate:
    case IssueFieldNames.modifiedBy:
      return true;
    case IssueFieldNames.startDateDelay:
      return hasIssueFieldValueForPdfEstimate(issue, IssueFieldNames.targetStartDate);
    case IssueFieldNames.responseToAuthorDelay:
      return hasIssueFieldValueForPdfEstimate(
        issue,
        IssueFieldNames.targetResponseToAuthorDate
      );
    case IssueFieldNames.amountPercentage:
      return hasIssueFieldValueForPdfEstimate(issue, IssueFieldNames.targetAmount);
    case IssueFieldNames.completionDateDelay:
      return hasIssueFieldValueForPdfEstimate(
        issue,
        IssueFieldNames.targetCompletionDate
      );
    case IssueFieldNames.numberOfEmployees:
      return hasNumberValue(issue.extendedData.numberOfEmployees);
    case IssueFieldNames.sickLeaveStartDate:
      return hasStringValue(issue.extendedData.sickLeaveStartDate);
    case IssueFieldNames.sickLeaveEndDate:
      return hasStringValue(issue.extendedData.sickLeaveEndDate);
    case IssueFieldNames.supervisor:
      return hasObjectIdValue(issue.extendedData.supervisor);
    case IssueFieldNames.victim:
      return hasStringValue(issue.extendedData.victim);
    case IssueFieldNames.victimJobTitle:
      return hasStringValue(issue.extendedData.victimJobTitle);
    case IssueFieldNames.employeeNumber:
      return hasStringValue(issue.extendedData.employeeNumber);
    case IssueFieldNames.employmentType:
      return hasStringValue(issue.extendedData.employmentType ?? undefined);
    case IssueFieldNames.immediateMeasures:
      return hasStringValue(issue.extendedData.immediateMeasures);
    case IssueFieldNames.victimActivity:
      return hasStringValue(issue.extendedData.victimActivity);
    case IssueFieldNames.injuryTypes:
      return hasArrayValue(issue.extendedData.injuryTypes);
    case IssueFieldNames.injuredBodyParts:
      return hasArrayValue(issue.extendedData.injuredBodyParts);
    case IssueFieldNames.applicationNumber:
      return hasStringValue(issue.extendedData.applicationNumber);
    case IssueFieldNames.productType:
      return hasStringValue(issue.extendedData.productType);
    case IssueFieldNames.producerDistributor:
      return hasStringValue(issue.extendedData.producerDistributor);
    case IssueFieldNames.typeModel:
      return hasStringValue(issue.extendedData.typeModel);
    case IssueFieldNames.installationLocation:
      return hasStringValue(issue.extendedData.installationLocation);
    case IssueFieldNames.requirements:
      return hasArrayValue(issue.extendedData.requirements);
    case IssueFieldNames.installationBasis:
      return hasArrayValue(issue.extendedData.installationBasis);
    case IssueFieldNames.constructionProductDocuments:
      return hasArrayValue(issue.extendedData.constructionProductDocuments);
    case IssueFieldNames.deviceDocuments:
      return hasArrayValue(issue.extendedData.deviceDocuments);
    case IssueFieldNames.additionalDocuments:
      return hasArrayValue(issue.extendedData.additionalDocuments);
    case IssueFieldNames.linkedDocumentation:
      return hasArrayValue(
        (issue.linkedDocumentation ?? []).filter((item) => !item.deleted)
      );
    case IssueFieldNames.priority:
      return hasStringValue(issue.extendedData.priority ?? undefined);
    case IssueFieldNames.projectStage:
      return hasStringValue(issue.extendedData.projectStage ?? undefined);
    default:
      return false;
  }
}

export function issueHasMainImageDocument(
  issue: IssueForPdfFieldValue,
  primaryImageCount: number
): boolean {
  if (primaryImageCount > 0) {
    return true;
  }

  if (!issue.mainImage) {
    return false;
  }

  const documents = issue.primaryData.documents ?? [];
  return documents.some((document) =>
    compareObjectIds(document._id, issue.mainImage)
  );
}
